import { GAMES_DOC, GAMES_MAP_KEY, gameMetaSchema, type GameMeta } from '@awwyeah/shared';
import { useEffect, useSyncExternalStore } from 'react';
import { useYDoc, useYMap } from '../../sync';
import { useUser } from '../auth';
import { useGameSummary } from './useGameMoves';
import { useUnreadChat } from './components/chatSeen';

/**
 * "Whose turn?" surfaced one level up (owner ask 2026-08-04: "make it easy to see when
 * it's your turn"): a count of active games waiting on you, shown as a badge on the Apps
 * tab and mirrored to the PWA app-icon badge where supported (Android). Passive facts you
 * opted into by joining games — no push, no nag (no-demand-mechanics safe).
 *
 * Mechanics: each active game needs its own doc subscription (hooks per doc), and the
 * number of games varies — so a hidden probe component per game feeds a tiny external
 * store, and consumers read the aggregated count. Probes render once in the AppShell.
 */
const turnByGame = new Map<string, boolean>();
const listeners = new Set<() => void>();
let cachedCount = 0;

function recount(): void {
  const next = [...turnByGame.values()].filter(Boolean).length;
  if (next === cachedCount) return;
  cachedCount = next;
  for (const l of listeners) l();
}

function setGameTurn(gameId: string, mine: boolean): void {
  if (turnByGame.get(gameId) === mine) return;
  turnByGame.set(gameId, mine);
  recount();
}

function dropGame(gameId: string): void {
  if (!turnByGame.has(gameId)) return;
  turnByGame.delete(gameId);
  recount();
}

export function useYourTurnCount(): number {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => cachedCount,
    () => 0,
  );
}

function TurnProbe({ game, meId }: { game: GameMeta; meId: string }): null {
  const summary = useGameSummary(game, meId);
  // A new chat line from the other player counts as "something waiting" too.
  const unreadChat = useUnreadChat(game.id, meId).count > 0;
  useEffect(() => {
    setGameTurn(game.id, (summary.isMyTurn && !summary.finished) || unreadChat);
    return () => dropGame(game.id);
  }, [game.id, summary.isMyTurn, summary.finished, unreadChat]);
  return null;
}

/** Mounted once in the AppShell: subscribes to active games and keeps the count + the
 * PWA app-icon badge honest. Renders nothing. */
export function YourTurnProbes(): JSX.Element | null {
  const { user } = useUser();
  const doc = useYDoc(GAMES_DOC);
  const { entries } = useYMap<GameMeta>(doc, GAMES_MAP_KEY);
  const count = useYourTurnCount();

  useEffect(() => {
    const nav = navigator as Navigator & {
      setAppBadge?: (n: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    if (!nav.setAppBadge) return;
    void (count > 0 ? nav.setAppBadge(count) : nav.clearAppBadge?.());
  }, [count]);

  if (!user) return null;
  const mine = entries
    .map(([, meta]) => gameMetaSchema.safeParse(meta))
    .flatMap((p) => (p.success ? [p.data] : []))
    .filter((g) => g.status !== 'done' && g.playerIds.includes(user.id));
  return (
    <>
      {mine.map((g) => (
        <TurnProbe key={g.id} game={g} meId={user.id} />
      ))}
    </>
  );
}
